import {useState} from 'react';
import {Button, Form, InputNumber, Select, Switch, Tag, Tooltip} from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {toast} from 'react-toastify';

import DBModal from 'components/modal/modal';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import FileUploadList from 'components/fileUploadList';
import {
  currencyOptions,
  formatMoney,
  formatRateValue,
  symbolFor,
} from 'utils/currencyFormatting';
import styles from './createSwapTransactionModal.module.scss';

const STEPS = [
  {key: 'form', label: 'Details'},
  {key: 'review', label: 'Review'},
];

/**
 * Manual swap in two passes: fill in the details, then read back what the
 * backend actually priced — rate, fees and discounts — before committing.
 * The preview and the real call are the same mutation, flagged by `preview`.
 */
const CreateSwapTransactionModal = ({
  isOpen,
  onClose,
  userId,
  onSuccess,
  mutation,
  refetch,
  loading: mutationLoading,
}) => {
  const [modalForm] = Form.useForm();
  const [user, setUser] = useState();
  const [step, setStep] = useState('form');
  const [formValues, setFormValues] = useState({});
  const [previewData, setPreviewData] = useState(null);

  const source = formValues?.sourceCurrency;
  const target = formValues?.targetCurrency;

  const buildInput = (values, preview) => ({
    userId: userId || user?.split(';')[2],
    amount: Number(values?.amount),
    sourceCurrency: values?.sourceCurrency,
    targetCurrency: values?.targetCurrency,
    preview,
    customExchangeRateInTargetCurrency:
      values?.useCustomExchangeRate &&
      values?.customExchangeRateInTargetCurrency
        ? Number(values.customExchangeRateInTargetCurrency)
        : undefined,
    ...(values?.files?.length ? {files: values.files} : {}),
  });

  const handleFieldValues = values => {
    const finalUserId = userId || user?.split(';')[2];
    if (!finalUserId) {
      toast.error('Select a user first');
      return;
    }

    setFormValues({...values});
    mutation({variables: {input: buildInput(values, true)}})
      .then(({data: {adminSwapCurrencies}}) => {
        if (adminSwapCurrencies.__typename === 'Error') {
          toast.error(adminSwapCurrencies.message);
          return;
        }
        setPreviewData(adminSwapCurrencies);
        setStep('review');
      })
      .catch(error =>
        toast.error(error?.message || 'Failed to preview swap transaction'),
      );
  };

  const handleCreateTransaction = () => {
    const finalUserId = userId || user?.split(';')[2];
    if (!finalUserId) {
      toast.error('Select a user first');
      return;
    }

    mutation({variables: {input: buildInput(formValues, false)}})
      .then(({data: {adminSwapCurrencies}}) => {
        if (adminSwapCurrencies.__typename === 'Error') {
          toast.error(adminSwapCurrencies.message);
          return;
        }
        toast.success('Swap transaction created');
        handleClose();
        refetch?.();
        onSuccess?.();
      })
      .catch(error =>
        toast.error(error?.message || 'Failed to create swap transaction'),
      );
  };

  const handleClose = () => {
    modalForm.resetFields();
    setFormValues({});
    setStep('form');
    setPreviewData(null);
    setUser();
    onClose();
  };

  /** Swap the two currency fields so the direction can be flipped in one click. */
  const flipCurrencies = () => {
    const sourceCurrency = modalForm.getFieldValue('sourceCurrency');
    const targetCurrency = modalForm.getFieldValue('targetCurrency');
    modalForm.setFieldsValue({
      sourceCurrency: targetCurrency,
      targetCurrency: sourceCurrency,
    });
    modalForm.validateFields(['sourceCurrency', 'targetCurrency']);
  };

  const differentFrom =
    otherField =>
    ({getFieldValue}) => ({
      async validator(_, value) {
        if (!value || getFieldValue(otherField) !== value) return;
        throw new Error('Source and target currency must be different');
      },
    });

  // Review rendering ----------------------------------------------------------

  const formatTierSchedule = schedule => {
    if (!schedule?.length) return null;
    return schedule
      .map(item => {
        if (item.applyTo === 'RATE') {
          const value =
            item.fixedFeeAmount != null
              ? item.fixedFeeAmount
              : item.variableFeeAmount != null
              ? `${item.variableFeeAmount}%`
              : '-';
          return `adjusts the exchange rate by ${symbolFor(target)}${value}`;
        }
        if (item.discountType === 'FIXED') {
          return `reduces the fixed fee by ${formatMoney(
            item.fixedFeeAmount ?? 0,
            source,
          )}`;
        }
        return `reduces the variable fee by ${item.variableFeeAmount ?? 0}%`;
      })
      .join(' and ');
  };

  const renderTierHighlights = () => {
    const tierFee = previewData?.metadata?.selectedTierFeeThreshold;
    const tierRate = previewData?.metadata?.selectedTierRateThreshold;
    if (!tierFee && !tierRate) return null;

    const tiers = [
      tierFee && {title: 'Tier fee adjustment applied', tier: tierFee},
      tierRate && {title: 'Tier rate adjustment applied', tier: tierRate},
    ].filter(Boolean);

    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Tier adjustments</h3>
        <div className={styles.tierGrid}>
          {tiers.map(({title, tier}) => (
            <div className={styles.tierCard} key={title}>
              <p className={styles.tierTitle}>
                <CheckCircleOutlined /> {title}
              </p>
              <p className={styles.tierLine}>
                Applies from {formatMoney(tier.amount, source)} upward.
              </p>
              <p className={styles.tierLine}>
                This tier {formatTierSchedule(tier.schedule)}.
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFeeSummary = () => {
    const feeSummary = previewData?.metadata?.swapFeeSummary;
    const originalFee =
      feeSummary?.feeWithoutDiscount ?? previewData?.actualFeeWithoutDiscount;
    const finalFee =
      feeSummary?.feeCharged ??
      feeSummary?.feeAfterAllDiscounts ??
      previewData?.feesWithDiscountApplied;
    const savedFee = feeSummary?.feeSaved ?? 0;

    if (originalFee == null && finalFee == null) return null;

    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Fees</h3>
        <div className={styles.feeSummary}>
          <div className={styles.feeSummaryItem}>
            <span className={styles.itemLabel}>Standard fee</span>
            <span
              className={
                savedFee > 0 ? styles.feeSlashedValue : styles.itemValue
              }>
              {formatMoney(originalFee, source)}
            </span>
          </div>
          <div className={styles.feeSummaryItem}>
            <span className={styles.itemLabel}>Fee charged</span>
            <span className={styles.feeFinalValue}>
              {formatMoney(finalFee, source)}
            </span>
          </div>
          {savedFee > 0 && (
            <div className={styles.feeSummaryItem}>
              <span className={styles.itemLabel}>Discount</span>
              <span className={styles.feeSavedValue}>
                −{formatMoney(savedFee, source)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFeeBreakdown = () => {
    const {feeSchedule, discount, appliedFeeSchedule} = previewData || {};
    const baseDiscountSchedule =
      previewData?.metadata?.swapDiscountSummary?.baseDiscountSchedule || [];
    const tierDiscountSchedule =
      previewData?.metadata?.swapDiscountSummary?.tierDiscountSchedule || [];

    if (
      !feeSchedule?.length &&
      !discount?.length &&
      !appliedFeeSchedule?.length
    )
      return null;

    const getFeeAmount = (list, type) => {
      const item = list?.find(entry => entry.feeType === type);
      if (!item) return null;
      return type === 'FIXED' ? item.fixedFeeAmount : item.variableFeeAmount;
    };
    const getDiscountAmount = (type, kind) => {
      const list =
        kind === 'base'
          ? baseDiscountSchedule?.length
            ? baseDiscountSchedule
            : discount
          : tierDiscountSchedule;
      const item = list?.find(entry => entry?.feeType === type);
      if (!item) return 0;
      const value =
        type === 'FIXED' ? item.fixedFeeAmount : item.variableFeeAmount;
      return value ?? 0;
    };
    const formatFeeValue = (value, type) =>
      type === 'VARIABLE' ? `${value ?? 0}%` : formatMoney(value ?? 0, source);
    const formatDiscountValue = (value, type) => {
      const formatted = formatFeeValue(value, type);
      return Number(value || 0) > 0 ? `−${formatted}` : formatted;
    };

    const rows = [
      ['VARIABLE', 'Variable fee'],
      ['FIXED', 'Fixed fee'],
    ].filter(([type]) =>
      [feeSchedule, discount, appliedFeeSchedule].some(list =>
        list?.some(entry => entry.feeType === type),
      ),
    );

    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Fee breakdown{' '}
          <Tooltip title="Final fee = standard fee − base discount − tier discount">
            <InfoCircleOutlined className={styles.sectionHint} />
          </Tooltip>
        </h3>
        <div className={styles.feeTable}>
          <div className={styles.feeTableHead}>
            <span>Type</span>
            <span>Standard</span>
            <span>Base discount</span>
            <span>Tier discount</span>
            <span>Final</span>
          </div>
          {rows.map(([type, label]) => (
            <div className={styles.feeTableRow} key={type}>
              <span data-label="Type">{label}</span>
              <span data-label="Standard">
                {formatFeeValue(getFeeAmount(feeSchedule, type), type)}
              </span>
              <span data-label="Base discount" className={styles.discount}>
                {formatDiscountValue(getDiscountAmount(type, 'base'), type)}
              </span>
              <span data-label="Tier discount" className={styles.discount}>
                {formatDiscountValue(getDiscountAmount(type, 'tier'), type)}
              </span>
              <span data-label="Final" className={styles.applied}>
                {formatFeeValue(getFeeAmount(appliedFeeSchedule, type), type)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReview = () => (
    <>
      <div className={styles.receipt}>
        <div className={styles.receiptSide}>
          <span className={styles.receiptLabel}>Debited</span>
          <span className={styles.receiptAmount}>
            {formatMoney(previewData?.amountPaid, source)}
          </span>
          <span className={styles.receiptCurrency}>{source}</span>
        </div>
        <ArrowRightOutlined className={styles.receiptArrow} />
        <div className={styles.receiptSide}>
          <span className={styles.receiptLabel}>Received</span>
          <span className={`${styles.receiptAmount} ${styles.receiptIn}`}>
            {formatMoney(previewData?.amountReceived, target, {decimals: 2})}
          </span>
          <span className={styles.receiptCurrency}>{target}</span>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Transaction</h3>
        <div className={styles.detailsGrid}>
          {user && (
            <div className={styles.item}>
              <span className={styles.itemLabel}>User</span>
              <span className={styles.itemValue}>
                {user?.split(';')[0]} {user?.split(';')[1]}
              </span>
            </div>
          )}
          <div className={styles.item}>
            <span className={styles.itemLabel}>Daba Pro</span>
            <span>
              <Tag color={previewData?.isUserDabaPro ? 'green' : 'default'}>
                {previewData?.isUserDabaPro ? 'Yes' : 'No'}
              </Tag>
            </span>
          </div>
          <div className={styles.item}>
            <span className={styles.itemLabel}>Converted</span>
            <span className={styles.itemValue}>
              {formatMoney(previewData?.amountSwapped, source)}
            </span>
          </div>
          <div className={styles.item}>
            <span className={styles.itemLabel}>Fee</span>
            <span className={styles.itemValue}>
              {formatMoney(previewData?.calculatedFees, source)}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Rate</h3>
        <div className={styles.detailsGrid}>
          <div className={styles.item}>
            <span className={styles.itemLabel}>Rate used</span>
            <span className={styles.itemValue}>
              1 {source} = {symbolFor(target)}
              {formatRateValue(previewData?.rateUsed)}
            </span>
          </div>
          <div className={styles.item}>
            <span className={styles.itemLabel}>Configured pair rate</span>
            <span className={styles.itemValue}>
              1 {source} = {symbolFor(target)}
              {formatRateValue(previewData?.configuredPairRate)}
            </span>
          </div>
          {previewData?.currentMarketRate != null && (
            <div className={styles.item}>
              <span className={styles.itemLabel}>Current market rate</span>
              <span className={styles.itemValue}>
                1 {source} = {symbolFor(target)}
                {formatRateValue(previewData?.currentMarketRate)}
              </span>
            </div>
          )}
          <div className={styles.item}>
            <span className={styles.itemLabel}>Custom rate</span>
            <span>
              <Tag
                color={
                  previewData?.usedCustomExchangeRate ? 'green' : 'default'
                }>
                {previewData?.usedCustomExchangeRate ? 'Yes' : 'No'}
              </Tag>
            </span>
          </div>
          {previewData?.customExchangeRateInTargetCurrency != null && (
            <div className={styles.item}>
              <span className={styles.itemLabel}>Custom rate entered</span>
              <span className={styles.itemValue}>
                1 {source} = {symbolFor(target)}
                {formatRateValue(
                  previewData.customExchangeRateInTargetCurrency,
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {renderFeeSummary()}
      {renderTierHighlights()}
      {renderFeeBreakdown()}
    </>
  );

  // Form rendering ------------------------------------------------------------

  const renderForm = () => (
    <Form
      onFinish={handleFieldValues}
      form={modalForm}
      layout="vertical"
      className={styles.form}>
      {!userId && (
        <Form.Item
          rules={[{required: !user, message: 'Select a user'}]}
          name="user"
          label="User">
          <UserSelectField
            mode="single"
            users={user}
            defaultUsers={user?.split(';')[2]}
            setUsers={setUser}
          />
        </Form.Item>
      )}

      <div className={styles.pairRow}>
        <Form.Item
          dependencies={['targetCurrency']}
          rules={[
            {required: true, message: 'Pick a source currency'},
            differentFrom('targetCurrency'),
          ]}
          name="sourceCurrency"
          className={styles.pairField}
          label="From">
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Source currency"
            options={currencyOptions}
          />
        </Form.Item>
        <Tooltip title="Swap the direction">
          <button
            type="button"
            className={styles.flipButton}
            aria-label="Swap the direction"
            onClick={flipCurrencies}>
            <SwapOutlined />
          </button>
        </Tooltip>
        <Form.Item
          dependencies={['sourceCurrency']}
          rules={[
            {required: true, message: 'Pick a target currency'},
            differentFrom('sourceCurrency'),
          ]}
          name="targetCurrency"
          className={styles.pairField}
          label="To">
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Target currency"
            options={currencyOptions}
          />
        </Form.Item>
      </div>

      <Form.Item
        noStyle
        shouldUpdate={(prev, current) =>
          prev.sourceCurrency !== current.sourceCurrency
        }>
        {({getFieldValue}) => (
          <Form.Item
            rules={[
              {required: true, message: 'Enter an amount'},
              {
                async validator(_, value) {
                  if (value === undefined || value === null) return;
                  if (Number(value) > 0) return;
                  throw new Error('The amount must be above zero');
                },
              },
            ]}
            name="amount"
            label="Amount to swap"
            extra="Fees are taken out of this amount, not added on top.">
            <InputNumber
              className={styles.fullWidth}
              min={0}
              stringMode
              addonAfter={getFieldValue('sourceCurrency') || '—'}
              placeholder="0.00"
            />
          </Form.Item>
        )}
      </Form.Item>

      <Form.Item
        name="useCustomExchangeRate"
        label="Override the exchange rate"
        valuePropName="checked"
        extra="Leave off to use the platform's configured rate for this pair."
        className={styles.switchField}>
        <Switch />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prev, current) =>
          prev.targetCurrency !== current.targetCurrency ||
          prev.sourceCurrency !== current.sourceCurrency ||
          prev.useCustomExchangeRate !== current.useCustomExchangeRate
        }>
        {({getFieldValue}) => {
          if (!getFieldValue('useCustomExchangeRate')) return null;
          const sourceCurrency = getFieldValue('sourceCurrency');
          const targetCurrency = getFieldValue('targetCurrency');
          return (
            <Form.Item
              name="customExchangeRateInTargetCurrency"
              label={
                sourceCurrency && targetCurrency
                  ? `Custom rate — ${targetCurrency} per 1 ${sourceCurrency}`
                  : 'Custom rate'
              }
              rules={[
                {
                  required: true,
                  message: 'Enter a rate, or turn the override off',
                },
              ]}>
              <InputNumber
                className={styles.fullWidth}
                min={0}
                step={0.0001}
                stringMode
                prefix={symbolFor(targetCurrency)}
                placeholder="0.00"
              />
            </Form.Item>
          );
        }}
      </Form.Item>

      <FileUploadList
        form={modalForm}
        name="files"
        label="Supporting files (optional)"
        bucket="campaign-bucket-images"
        required={false}
      />

      <div className={styles.footer}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          htmlType="submit"
          className={styles.primaryButton}
          loading={mutationLoading}>
          Preview swap
        </Button>
      </div>
    </Form>
  );

  const isReview = step === 'review';

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={handleClose}
      width={isReview ? 760 : 560}
      content={
        <div className={styles.modal}>
          <div className={styles.header}>
            <h1 className={styles.title}>Create a swap manually</h1>
            <p className={styles.subtitle}>
              {isReview
                ? 'Nothing has been created yet. Check the numbers, then confirm.'
                : 'Record a currency swap on a user’s behalf.'}
            </p>
            <div className={styles.steps}>
              {STEPS.map((item, index) => (
                <div
                  key={item.key}
                  className={`${styles.step} ${
                    step === item.key ? styles.stepActive : ''
                  } ${isReview && index === 0 ? styles.stepDone : ''}`}>
                  <span className={styles.stepIndex}>{index + 1}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {isReview ? (
            <>
              <div className={styles.scrollableContent}>{renderReview()}</div>
              <div className={styles.footer}>
                <Button onClick={() => setStep('form')}>Back</Button>
                <Button
                  type="primary"
                  className={styles.primaryButton}
                  loading={mutationLoading}
                  onClick={handleCreateTransaction}>
                  Confirm and create
                </Button>
              </div>
            </>
          ) : (
            renderForm()
          )}
        </div>
      }
    />
  );
};

export default CreateSwapTransactionModal;
